/**
 * Centralized z-index scale for the design system.
 *
 * Tier 1 (0-99):      Page-level elements (sidebar, sticky headers)
 * Tier 2 (1000-1099):  Navigation chrome (navbar, navbar dropdowns)
 * Tier 3 (1100-1199):  Floating UI (dropdowns, combobox, datepicker, filter, tooltip)
 * Tier 4 (1200-1299):  Sticky overlays (action bar, footer)
 * Tier 5 (2000+):      Stackable layers (drawer, modal, alert dialog)
 *                       — managed by LayerManager with dynamic offsets
 * Tier 6 (9000-9999):  System-level (command palette, toast, loading indicator)
 *
 * Stackable layers (drawer, modal, alertDialog) use base values here
 * combined with a runtime offset from LayerManager to guarantee
 * correct stacking when multiple instances are open simultaneously.
 */
export const zIndex = {
  hide: -1,
  base: 0,

  sidebar: 100,
  sidebarOverlay: 99,
  sidebarToggle: 200,
  sidebarMobileBackdrop: 101,

  navbar: 1000,
  navbarBackdrop: 998,
  navbarMobile: 999,
  navbarDropdown: 1010,

  dropdown: 1100,
  combobox: 1100,
  datepicker: 1100,
  filter: 1100,
  tooltip: 1150,

  sticky: 1200,

  drawer: 2000,
  modal: 3000,
  alert: 4000,

  commandPalette: 9000,
  loading: 9500,
  toast: 9800,
} as const;

export type ZIndexToken = keyof typeof zIndex;

/**
 * Spacing between base z-index tiers for stackable layers.
 * Each new stacked instance increments by 1 within its tier.
 * The gap between tiers (1000) ensures an alert always beats a modal,
 * even with many nested instances.
 */
export const LAYER_GAP = 1000;
